/**
 * Network printer addresses, the same rule everywhere: the staff apps' forms,
 * staff web's Print Bridge client and the API (P12-T06). Print Bridge itself
 * enforces the same in Go (`apps/print-bridge/internal/policy`).
 */

/** A LAN printer's address: `host:port`, port 9100 unless given. */
export type LanAddress = { host: string; port: number };

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/**
 * Reads a typed or stored LAN address: an IPv4 address in a private range
 * (10/8, 172.16/12, 192.168/16) with an optional port from 9100 to 9109, as
 * Print Bridge allows. Null when it is not one. The host comes back without
 * leading zeros, which some resolvers read as octal.
 */
export function parseLanAddress(text: string | null | undefined): LanAddress | null {
	const [hostPart, portPart, ...rest] = (text ?? "").trim().split(":");
	if (!hostPart || rest.length > 0) return null;
	const m = IPV4.exec(hostPart);
	if (!m) return null;
	const parts = m.slice(1).map(Number);
	if (parts.some((n) => n > 255)) return null;
	const [a = 0, b = 0] = parts;
	const isPrivate = a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
	if (!isPrivate) return null;
	const port = portPart === undefined || portPart === "" ? 9100 : Number(portPart);
	if (!Number.isInteger(port) || port < 9100 || port > 9109) return null;
	return { host: parts.join("."), port };
}

/** The stored form of a LAN address. */
export function formatLanAddress(address: LanAddress): string {
	return `${address.host}:${address.port}`;
}
